import { useState, useEffect } from "react";
import { animate } from "framer-motion";
import { Users, ArrowRight } from "lucide-react";
import { PRICE_PER_PERSON } from "../../data";

const TIERS = [
  { v: 25, l: "-5%" },
  { v: 35, l: "-10%" },
];

export const BudgetCalculator = () => {
  const [count, setCount] = useState(30);
  const discountRate = count >= 35 ? 0.1 : count >= 25 ? 0.05 : 0;
  const base = count * PRICE_PER_PERSON;
  const total = Math.round(base * (1 - discountRate));
  const perPerson = Math.round(PRICE_PER_PERSON * (1 - discountRate));
  const fmt = (n) => n.toLocaleString("fr-FR");
  const pct = ((count - 10) / (150 - 10)) * 100;

  const [display, setDisplay] = useState(total);
  useEffect(() => {
    const controls = animate(display, total, {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1],
      onUpdate: (v) => setDisplay(v),
    });
    return () => controls.stop();
  }, [total]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="rounded-3xl bg-ocean p-6 sm:p-7 text-white" data-testid="budget-calculator">
      <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.2em] font-semibold">
        <Users className="w-4 h-4" /> Estimez votre budget
      </div>

      <div className="mt-5 flex items-end justify-between">
        <div>
          <div className="text-sm text-white/60">Participants</div>
          <div className="font-serif text-4xl font-semibold" data-testid="calc-participants">{count}</div>
        </div>
        <div className="text-right">
          <div className="flex items-center justify-end gap-2 text-sm text-white/60">
            Budget total estimé
            {discountRate > 0 ? (
              <span className="text-[10px] font-bold text-ocean-dark bg-gold rounded-full px-2 py-0.5" data-testid="calc-discount-badge">
                -{Math.round(discountRate * 100)}%
              </span>
            ) : null}
          </div>
          {discountRate > 0 ? (
            <div className="text-sm text-white/40 line-through" data-testid="calc-base-total">{fmt(base)}€</div>
          ) : null}
          <div className="font-serif text-4xl font-semibold text-gold tabular-nums" data-testid="calc-total">
            {fmt(Math.round(display))}€
          </div>
        </div>
      </div>

      <div className="relative mt-5">
        <input
          type="range"
          min={10}
          max={150}
          step={5}
          value={count}
          onChange={(e) => setCount(Number(e.target.value))}
          className="relative z-10 w-full h-2 rounded-full appearance-none cursor-pointer accent-coral"
          style={{ background: `linear-gradient(to right, #E65C00 ${pct}%, rgba(255,255,255,0.15) ${pct}%)` }}
          data-testid="calc-slider"
        />
        {TIERS.map((t) => (
          <span
            key={t.v}
            className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-1 h-3.5 bg-white/80 rounded-full pointer-events-none"
            style={{ left: `${((t.v - 10) / 140) * 100}%` }}
          />
        ))}
      </div>
      <div className="relative mt-2 h-7 text-[10px]">
        <span className="absolute left-0 text-white/40">10</span>
        {TIERS.map((t) => (
          <div
            key={t.v}
            className="absolute -translate-x-1/2 text-center leading-tight"
            style={{ left: `${((t.v - 10) / 140) * 100}%` }}
            data-testid={`calc-tier-${t.v}`}
          >
            <div className="text-gold font-semibold">{t.l}</div>
            <div className="text-white/40">{t.v} pers.</div>
          </div>
        ))}
        <span className="absolute right-0 text-white/40">150</span>
      </div>

      <div className="mt-4 flex items-center justify-between text-xs text-white/55 border-t border-white/10 pt-4">
        <span>
          Soit {fmt(perPerson)}€ / collaborateur, tout inclus
          {discountRate > 0 ? <span className="text-gold font-medium"> · remise {Math.round(discountRate * 100)}% appliquée</span> : ""}
        </span>
      </div>
      <button
        onClick={() => {
          window.dispatchEvent(new CustomEvent("prefill-effectif", { detail: { count } }));
          document.querySelector("#formulaire-devis")?.scrollIntoView({ behavior: "smooth" });
        }}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-coral hover:bg-coral-hover text-white font-semibold px-6 py-3.5 text-sm transition-all hover:-translate-y-0.5"
        data-testid="calc-cta-button"
      >
        Valider ce budget avec un devis
        <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
};
